import React, { useEffect, useMemo, useState } from 'react';
import TableComponent from 'app/components/TableComponent';
import { Modal } from 'react-bootstrap-v5';
import './styles/roles.css';
import { KTSVG } from '_metronic/helpers/components/KTSVG';
import { getAllRoles, getAllRolesFilter, getAllRolesFilterDesc, roleCreateAndUpdate } from 'app/service/configuration/Roles';
import { RolesType } from 'app/types/configuracion/roles';
import { FilterView } from 'app/components/FilterView';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { InputModalText } from 'app/components/InputsComponents/InputModalText';
import { InputModalSelect } from 'app/components/InputsComponents/InputModalSelect';
import { ESTATUS } from 'app/utilities/constants';

const heads = [
    { code: 'code', label: 'Código' },
    { code: 'description', label: 'Descripción' },
    { code: 'status', label: 'Estatus' },
];

const RolesView: React.FC = () => {
    const [submitWithoutStatusValidation, setSubmitWithoutStatusValidation] = useState(false);
    const [show, setShow] = useState(false);
    const [showFilterModal, setShowFilterModal] = useState<boolean>(false);
    const [loadingGrid, setLoadingGrid] = useState<boolean>(true);
    const [isFiltered, setIsFiltered] = useState<boolean>(false);

    const [employeeName, setEmployeeName] = useState<string>('');
    const [employeeNumber, setEmployeeNumber] = useState<string>('');
    const [selectedOption, setSelectedOption] = useState<string>('');

    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(5);
    const [totalRecords, setTotalRecords] = useState<number>(0);

    const [roles, setRoles] = useState<RolesType[]>([]);
    const [id, setId] = useState<number | undefined | null>(null);

    const RolesSchema = Yup.object().shape({
        code: Yup.string()
            .required('Este campo es requerido')
            .min(3, "Mínimo 3 caracter")
            .max(5, "Máximo 5 caracteres"),
        description: Yup.string()
            .required('Este campo es requerido')
            .min(3, "Mínimo 3 caracteres")
            .max(101, "Máximo 100 caracteres"),
        obligatory: Yup.number()
            .when([], {
                is: () => !submitWithoutStatusValidation,
                then: Yup.number()
                    .required('Este campo es requerido')
                    .notOneOf([0], "Seleccione una opción"),
                otherwise: Yup.number(),
            }),
    });

    const formRoles = useFormik<any>({
        validationSchema: RolesSchema,
        initialValues: {
            code: '',
            description: '',
            obligatory: 0,
        },
        onSubmit: async (values, { setSubmitting }) => {
            const { description, code } = values;
            try {
                await roleCreateAndUpdate(
                    {
                        ...values,
                        code: code.toUpperCase(),
                        description: description,
                        status: Number(values?.obligatory),
                        id: id
                    }
                );

                setEmployeeName('');
                setEmployeeNumber('');
                setSelectedOption('');
                setIsFiltered(false);

                toggleRolesModal();
                fetchRoles();
            } catch (err) {
                console.error('Error', err);
            } finally {
                setSubmitting(false);
            }
            formRoles.resetForm();
        },
    });

    const fetchRoles = async (): Promise<void> => {
        try {
            const res = await getAllRoles(currentPage, pageSize);

            setRoles((res!.records ?? []).map((record) => ({
                ...record,
                status: record?.statusId === 1 ? 'Activo' : 'Inactivo',
            })));

            setTotalRecords(res!.totalRecords);

            setLoadingGrid(false);
        } catch (err) {
            console.error("Error:", err);
        }
    }

    useEffect(() => {
        if (isFiltered) {
            if (selectedOption === '1' && employeeName) {
                getAllRolesFilter(currentPage, pageSize, employeeName).then(res => {
                    if (res === null) {
                        setTotalRecords(0);
                        return setRoles([]);
                    }
                    setRoles((res!.records ?? []).map((record) => ({
                        ...record,
                        status: record?.statusId === 1 ? 'Activo' : 'Inactivo',
                    })));
                    setTotalRecords(res!.totalRecords ?? 0);
                });
            }

            if (selectedOption === '2' && employeeNumber) {
                getAllRolesFilterDesc(currentPage, pageSize, employeeNumber).then(res => {
                    if (res === null) {
                        setTotalRecords(0);
                        return setRoles([]);
                    }
                    setRoles((res!.records ?? []).map((record) => ({
                        ...record,
                        status: record?.statusId === 1 ? 'Activo' : 'Inactivo',
                    })));
                    setTotalRecords(res!.totalRecords ?? 0);
                });
            }
        } else {
            fetchRoles();
        }
    }, [currentPage, pageSize, isFiltered, employeeName, employeeNumber]);

    const rows = useMemo(() => {
        if (!loadingGrid && roles.length) {
            return roles.map((row) => ({
                code: (
                    <div
                        style={{ textAlign: 'center' }}
                    >
                        {row.code}
                    </div>
                ),
                description: (
                    <div
                        onClick={() => {
                            toggleRolesModal();
                            setSubmitWithoutStatusValidation(false);
                            formRoles.setValues({ code: row.code, description: row.description, obligatory: row.statusId });
                            setId(row.id);
                        }}
                        style={{ cursor: 'pointer', color: 'blue' }}
                    >
                        {row.description}
                    </div>
                ),
                status: (
                    <div style={{ textAlign: 'center' }}>{row.status}</div>
                ),
            }));
        }
        return [];
    }, [roles, loadingGrid]);

    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedOption(e.target.value);
    };

    const toggleRolesModal = () => {
        setId(null);
        setShow((prev) => !prev);
        formRoles.resetForm();
    }

    const toggleFilterModal = () => {
        setId(null);
        setShowFilterModal((prev) => !prev);
    };

    return (
        <div className="row gy-5 g-xl-8">
            <div className="col-xxl-12 col-xl-12 col-md-12 col-lg-12">
                <div className="card card-flush p-5">
                    <div className="d-flex justify-content-between align-items-center mb-5">
                        <h3 className="fw-bold fs-4">Roles</h3>
                        <div className="d-flex justify-content-end gap-2">
                            <button
                                className="btn btn-light-primary border-btn"
                                onClick={() => { setSubmitWithoutStatusValidation(true); toggleRolesModal(); }}
                            >
                                <KTSVG
                                    path="/media/icons/duotune/abstract/abs011.svg"
                                    className="svg-icon-2 svg-icon-gray-500 "
                                />
                            </button>
                            <button className="btn btn-light-primary border-btn w-10" onClick={toggleFilterModal}>
                                <KTSVG
                                    path="/media/icons/duotune/general/gen021.svg"
                                    className="svg-icon-5 svg-icon-gray-500"
                                />
                                <span className="ms-2">Filtrar</span>
                            </button>
                        </div>
                    </div>
                    <TableComponent
                        heads={heads}
                        rows={rows}
                        rowsPerPage={pageSize}
                        page={currentPage}
                        totalRows={totalRecords}
                        setPage={setCurrentPage}
                    />

                </div>
            </div>

            {/* Modal para roles */}
            <Modal
                id="kt_modal_roles"
                tabIndex={-1}
                dialogClassName="modal-dialog"
                show={show}
                onHide={toggleRolesModal}
            >
                <div className="modal-content">
                    <div className="modal-header">
                        <h1 className="text-muted">Roles</h1>
                        <div
                            className="btn btn-sm btn-icon btn-active-color-primary"
                            onClick={toggleRolesModal}
                        >
                            <KTSVG
                                className="svg-icon-2"
                                path="/media/icons/duotune/arrows/arr061.svg"
                            />
                        </div>
                    </div>
                    <div className="modal-body mx-5">
                        <form className="form needs-validation" noValidate
                            onSubmit={(e) => { e.preventDefault(); formRoles.handleSubmit(); }}>
                            <InputModalText
                                formFormik={formRoles}
                                label={'Código'}
                                htmlFor={'code'}
                                name={'code'}
                                placeholder={'Ingrese código'}
                                width='100'
                                styleBootstrapClasses='mb-5'
                            />
                            <InputModalText
                                formFormik={formRoles}
                                label={'Descripción'}
                                htmlFor={'description'}
                                name={'description'}
                                placeholder={'Ingrese descripción'}
                                width='100'
                                styleBootstrapClasses='mb-5'
                            />
                            {/* Estado */}
                            <InputModalSelect
                                selectOptions={ESTATUS}
                                formFormik={formRoles}
                                label={'Estatus'}
                                htmlFor={'obligatorio'}
                                name={'obligatory'}
                                width={'100'}
                                show={!submitWithoutStatusValidation}
                            />
                            {/* Botones */}
                            <div className="d-flex justify-content-between mt-4">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={toggleRolesModal}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-danger"
                                    style={{ backgroundColor: '#ff4d4f', borderColor: '#ff4d4f' }}
                                >
                                    Guardar
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </Modal>

            {/* Modal del Filtro */}
            <Modal
                id="kt_modal_filter"
                tabIndex={-1}
                dialogClassName="modal-dialog"
                show={showFilterModal}
                onHide={toggleFilterModal}
            >
                {showFilterModal && (
                    <FilterView
                        handleFilterClose={toggleFilterModal}
                        handleSelectChange={handleSelectChange}
                        selectedOption={selectedOption}
                        employeeName={employeeName}
                        employeeNumber={employeeNumber}
                        setEmployeeName={setEmployeeName}
                        setEmployeeNumber={setEmployeeNumber}
                        setSelectedOption={setSelectedOption}
                        setCurrentPage={setCurrentPage}
                        setIsFiltered={setIsFiltered}
                        fetchData={() => fetchRoles()}
                        fetchFilterData={() => getAllRolesFilter(currentPage, pageSize, employeeName)}
                        fetchFilterData2={() => getAllRolesFilterDesc(currentPage, pageSize, employeeNumber)}
                    />
                )}
            </Modal>
        </div>
    );
};

export { RolesView };
